<script lang="ts">
	import "./app.postcss";
	import { onMount } from "svelte";
	import {
		DarkMode,
		Navbar,
		NavBrand,
		NavHamburger,
		Sidebar,
		SidebarGroup,
		SidebarItem,
		SidebarWrapper,
		CloseButton,
		Alert,
		Spinner,
		Button,
	} from "flowbite-svelte";
	import { sineIn } from "svelte/easing";
	import { slide } from "svelte/transition";
	import { Heading, P } from "flowbite-svelte";

	import Router, { push } from "./svelte-spa-router";
	import { location } from "./svelte-spa-router";
	import { startAnalytics, trackPageView } from "./stores/analytics";
	import { activeSidebarPath } from "./Utilities";

	import routes from "./routes";

	import site from "./site";

	import { isConfigurationLoaded, configurationError } from "./AppConfiguration";
	import AbstractID, {
		isAuthorizationPending,
		isAuthorized,
		storable,
	} from "./AbstractID";
	import Drawer from "./components/Drawer.svelte";
	import AbstractIdCard from "./components/AbstractIDCard.svelte";
	import ToastContainer from "./components/ToastContainer.svelte";
	import ThemeSwitcherButton from "./components/ThemeSwitcherButton.svelte";

	const breakPoint: number = 640;
	let width: number = breakPoint;
	let isSidebarBackdropEnabled: boolean = true;
	let isSidebarHideOnClickOutside = true;

	let drawerHidden: boolean = false;
	$: if (width >= breakPoint) {
		drawerHidden = false;
		isSidebarHideOnClickOutside = false;
		isSidebarBackdropEnabled = false;
	} else {
		drawerHidden = true;
		isSidebarHideOnClickOutside = true;
		isSidebarBackdropEnabled = true;
	}
	/**
	 * Storefront analytics (Design §12).
	 *
	 * Mounted once, here, because the page-lifecycle hooks it installs have to outlive every route:
	 * the most interesting event in a funnel is routinely the last one before somebody left, and a
	 * listener owned by a page is gone by the time that happens.
	 *
	 * The page-view emitter subscribes to the router's own `location`, so a client-side navigation
	 * is recorded — there is no page load to hook, which is the whole point of a history-mode SPA
	 * and the reason a naive tracker records one view per visit. `/Settings/*` records nothing:
	 * administration is not shopping, and an operator clicking through their own catalogue would
	 * otherwise be the largest source of product views on a young store.
	 */
	startAnalytics();
	location.subscribe(path => trackPageView(path));

	onMount(() => {
		if (width >= breakPoint) {
			drawerHidden = false;
			isSidebarHideOnClickOutside = false;
		} else {
			drawerHidden = true;
			isSidebarHideOnClickOutside = true;
		}
	});
	const fnToggleSide = (event?: Event, href: any = undefined) => {
		if (width < breakPoint) {
			drawerHidden = !drawerHidden;
		}

		if (href) {
			event?.preventDefault();
			push(href);
		}
	};
	const fnToggleDrawer = () => {
		drawerHidden = false;
	};

	/**
	 * The sidebar's sign-in control (Design §14).
	 *
	 * **Regular sign-in always leaves for AbstractID.** The storefront has one login flow of its
	 * own and it lives on `/Checkout/Start`, where it exists so that buying does not require an
	 * account first. Everywhere else identity belongs to the identity provider: it is what holds
	 * passwords, passkeys and second factors, and a second way in rendered on a shop is both a
	 * thing to keep in step and a form customers learn to expect from a page that is not the one
	 * they trust with it.
	 *
	 * The drawer is closed on the way, as every other action in this list does, so the redirect
	 * does not happen behind an open overlay.
	 */
	const fnSignIn = (event?: Event) => {
		fnToggleSide(event);
		AbstractID.beginSignIn(window.location.pathname);
	};

	/**
	 * Every link in the sidebar. Flowbite's `SidebarItem` matches `href === activeUrl` exactly, so
	 * the value below has to be one of these, and the list has to stay in step with the markup —
	 * a link missing here simply never lights up.
	 */
	const sidebarHrefs: string[] = [
		"/",
		// `/Account` covers the purchases page and every subscription detail page under it. The
		// two longer entries below are declared after it and win for their own locations, being
		// longer prefixes — which is the whole of how `activeSidebarPath` decides.
		"/Account",
		"/Account/Invoices",
		"/Account/Billing",
		// Administration is not listed here. Every admin screen is a `/Settings/Admin/*` page
		// reached from Settings' own second-level sidebar, exactly as the overview and the audit
		// log always were — so `Settings` is the link that lights for all of them, and it does so
		// by being their longest declared prefix.
		"/Settings"
	];

	let activeURL: string = "";
	$: activeURL = activeSidebarPath($location, sidebarHrefs);

	// Collapsed state of the sidebar sections, persisted per browser.
	const isAccountSectionOpen = storable<boolean>("sidebar.isAccountSectionOpen", true);

	/**
	 * Rail mode: the sidebar collapses to a 60px strip of icons.
	 *
	 * Everything in the strip — brand badge, toggle, nav icons — sits on the rail's
	 * centre axis. That requires labels to be `hidden` rather than merely
	 * transparent: an invisible label still occupies width and would push the icon
	 * off centre. Each item carries a `title`, which is the only label left once
	 * the text is gone.
	 */
	const isSidebarRail = storable<boolean>("sidebar.isRail", false);
</script>

<svelte:window bind:innerWidth={width} />

<header class="flex-none w-full mx-auto bg-white dark:bg-zinc-800">
	<Navbar class="bg-zinc-100 dark:bg-zinc-800 border-solid border-t-[4px] border-primary-500 dark:border-primary-500" hidden={!drawerHidden} let:toggle>
		<NavHamburger
			on:click={fnToggleDrawer}
			btnClass="focus:outline-none whitespace-normal rounded-lg focus:ring-2 p-1.5 focus:ring-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 m-0 mr-3 lg:hidden}"
		/>
		<NavBrand href="/" class="lg:ml-64">
			<span class="text-gradient-primary text-xl font-bold uppercase">{site.name}</span>
		</NavBrand>
		<div class="flex items-center ml-auto">
			<DarkMode class="inline-block text-black dark:hover:text-white hover:text-gray-900" />
		</div>
	</Navbar>
</header>

<Drawer
	transitionType="fly"
	divClass="overflow-y-auto z-40 text-white bg-depth"
	transitionParams={{ x: -250, duration: 200, easing: sineIn }}
	bind:backdrop={isSidebarBackdropEnabled}
	bind:hidden={drawerHidden}
	bind:activateClickOutside={isSidebarHideOnClickOutside}
	width={$isSidebarRail ? "w-[60px]" : "w-[250px]"}
	class="overflow-scroll pb-32"
	id="sidebar"
>
	<div class="flex items-center">
		<CloseButton on:click={() => (drawerHidden = true)} class="mt-2 -mb-4 text-white sm:hidden" />
	</div>
	<Sidebar asideClass={$isSidebarRail ? "" : "w-54"} activeUrl={activeURL}>
		<!-- In rail mode the horizontal padding halves, so a 36px brand badge still
		     fits inside a 60px strip and can sit on the same centre line as the 20px
		     nav icons. -->
		<SidebarWrapper divClass="overflow-y-auto overflow-x-hidden py-4 {$isSidebarRail ? 'px-2' : 'px-4'} rounded">
			{@const railCenter = $isSidebarRail ? "justify-center" : ""}
			{@const activeClass = `flex items-center ${railCenter} p-1.5 text-sm font-semibold rounded-lg dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 text-primary-500 bg-primary-100 dark:bg-primary-600/20 my-0.5 active`}
			{@const nonActiveClass = `flex items-center ${railCenter} p-1.5 text-sm font-medium text-gray-700 rounded-lg dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 my-0.5`}
			{@const iconClass = "w-5 min-w-5 shrink-0 transition"}
			{@const sectionTitleClass = "ml-2 mt-4 text-sm font-medium flex justify-between hover:text-primary-500 text-black dark:text-white cursor-pointer"}
			<!-- `hidden`, not `opacity-0`: an invisible label still occupies width,
			     which would push the icon off the rail's centre line. -->
			{@const labelClass = `ml-3 truncate ${$isSidebarRail ? "hidden" : ""}`}

			<!-- Brand row, and the collapse toggle that ends it. Expanded, the spacer
			     between them pushes the toggle to the right edge; in rail mode the row
			     turns into a column, so the badge keeps the centre line and the toggle
			     becomes its own full-width row beneath it.

			     The toggle sits *in* this row rather than being absolutely positioned
			     over the corner: an absolute toggle is outside the flow, so nothing
			     holds it apart from the app name and it lands on top of the wordmark
			     at any width where the two would meet. -->
			<div class="text-lg flex gap-2 items-center" class:flex-col={$isSidebarRail} class:justify-center={$isSidebarRail}>
				<a title={site.name} class="text-lg flex gap-2 items-center min-w-0" href={site.href}>
					<div class="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-orange-500 flex items-center justify-center text-white shadow-md shadow-primary-500/30 shrink-0">
						<i class="fa-solid fa-cart-shopping-fast text-gray-200 dark:text-zinc-800" />
					</div>
					{#if !$isSidebarRail}
						<!-- `whitespace-nowrap`, because the name is two words and the row
						     is tight: the expanded sidebar has 218px between its padding
						     for a 36px badge, this wordmark and the collapse toggle, and
						     a wrap would put "Store" on a line of its own and shove the
						     toggle out of the row. Title case rather than `uppercase` for
						     the same reason — the capitals are what overran the row. -->
						<span class="text-gradient-primary font-bold whitespace-nowrap">
							{site.shortname}
						</span>
					{/if}
				</a>
				{#if !$isSidebarRail}
					<div class="grow" />
				{/if}

				<!-- An inline SVG rather than a Font Awesome glyph: `h-4` does nothing
				     to an inline `<i>`, so the glyph aligned on its baseline and sat
				     high in the button. An SVG honours the box and centres properly.
				     The mark is a panel with an arrow, which reads as "sidebar" far
				     better than a bare chevron. -->
				<Button
					on:click={() => ($isSidebarRail = !$isSidebarRail)}
					color="alternative"
					title={$isSidebarRail ? "Expand sidebar" : "Collapse sidebar"}
					class="text-gray-600 dark:text-white grow-0 shrink-0 bg-transparent text-xs p-0 border-none {$isSidebarRail ? 'w-full' : ''}"
				>
					<svg class="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
						<path
							stroke="currentColor"
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d={$isSidebarRail
								? "m6 10 1.99994 1.9999-1.99994 2M11 5v14m-7 0h16c.5523 0 1-.4477 1-1V6c0-.55228-.4477-1-1-1H4c-.55228 0-1 .44772-1 1v12c0 .5523.44772 1 1 1Z"
								: "M7.99994 10 6 11.9999l1.99994 2M11 5v14m-7 0h16c.5523 0 1-.4477 1-1V6c0-.55228-.4477-1-1-1H4c-.55228 0-1 .44772-1 1v12c0 .5523.44772 1 1 1Z"}
						/>
					</svg>
				</Button>
			</div>

			{#if !$configurationError}
				<!-- Storefront navigation, visible signed in or out (Design §14): D9 requires
				     browsing and comparing without an account, so these are the only nav items a
				     first-time visitor sees. -->
				<SidebarGroup ulClass="mt-8 appNav">
					<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} title="Store" label="Store" href="/" on:click={(event) => fnToggleSide(event, "/")}>
						<svelte:fragment slot="icon">
							<i class="fa-solid fa-home {iconClass}"></i>
						</svelte:fragment>
					</SidebarItem>
				</SidebarGroup>

				{#if $isAuthorized}
					<!-- Account (Design §9, §14). Signed-in only: every page behind these links calls a
					     `requireAuth` endpoint, so offering them signed out would advertise a redirect. -->
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<!-- svelte-ignore a11y-no-static-element-interactions -->
					<div class:hidden={$isSidebarRail} class="{sectionTitleClass} {!$isAccountSectionOpen ? 'opacity-50' : ''}" on:click={() => ($isAccountSectionOpen = !$isAccountSectionOpen)}>
						Account
						<i class="fa-solid {$isAccountSectionOpen ? 'fa-angle-down' : 'fa-angle-up'}" />
					</div>
					{#if $isAccountSectionOpen || $isSidebarRail}
						<div transition:slide={{}}>
							<SidebarGroup ulClass="mt-0 appNav">
								<!--
									One entry, and it is the account landing page. Subscriptions, one-time
									purchases and what the customer is entitled to are one question — "what
									have I got from this store" — and splitting them across "Your account"
									and "Purchases" made each screen hold half the answer and link to the
									other. `/Account` is the href because it is the canonical path and the
									shortest prefix of the detail pages, so a subscription's own page lights
									this entry rather than nothing.
								-->
								<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} title="Purchases" label="Purchases" href="/Account" on:click={(event) => fnToggleSide(event, "/Account")}>
									<svelte:fragment slot="icon">
										<i class="fa-solid fa-bag-shopping {iconClass}"></i>
									</svelte:fragment>
								</SidebarItem>
								<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} title="Invoices" label="Invoices" href="/Account/Invoices" on:click={(event) => fnToggleSide(event, "/Account/Invoices")}>
									<svelte:fragment slot="icon">
										<i class="fa-solid fa-file-invoice {iconClass}"></i>
									</svelte:fragment>
								</SidebarItem>
								<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} title="Billing" label="Billing" href="/Account/Billing" on:click={(event) => fnToggleSide(event, "/Account/Billing")}>
									<svelte:fragment slot="icon">
										<i class="fa-solid fa-wallet {iconClass}"></i>
									</svelte:fragment>
								</SidebarItem>
							</SidebarGroup>
						</div>
					{/if}
				{/if}

				{#if $isAuthorized}
					<SidebarGroup ulClass="mt-8 appNav">
						<SidebarItem {activeClass} {nonActiveClass} spanClass={labelClass} title="Settings" label="Settings" href="/Settings" on:click={(event) => fnToggleSide(event, "/Settings")}>
							<svelte:fragment slot="icon">
								<i class="fa-solid fa-gear {iconClass}"></i>
							</svelte:fragment>
						</SidebarItem>
					</SidebarGroup>
				{/if}
			{/if}

			{#if $isAuthorized && !$isSidebarRail}
				<div class="my-4">
					<AbstractIdCard minimal compact showLinks={false} on:click={() => push("/Settings/Account")} />
				</div>
			{:else if !$isAuthorized && !$isAuthorizationPending && !$isSidebarRail}
				<!-- Signed-out chrome (Design §14). The sign-in redirect is **explicit**:
				     `initialize()` no longer performs it, so without this control a visitor on a
				     public route would have no way to sign in at all. It goes to AbstractID, like
				     every regular sign-in — the storefront's own login flow exists only on the
				     checkout page, so that buying needs no account first. -->
				<div class="my-4">
					<Button color="primary" class="w-full" on:click={fnSignIn}>
						<i class="fa-solid fa-right-to-bracket mr-2" />Sign in
					</Button>
				</div>
			{:else if !$isAuthorized && !$isAuthorizationPending && $isSidebarRail}
				<div class="my-4 flex justify-center">
					<Button color="primary" title="Sign in" class="!p-2 w-full" on:click={fnSignIn}>
						<i class="fa-solid fa-right-to-bracket {iconClass}" />
					</Button>
				</div>
			{:else}
				<!-- The card carries the gap between the nav and the controls below it.
				     When it isn't rendered — rail mode, or still resolving auth — this stands in,
				     otherwise the theme button crowds the last nav icon. An explicit
				     height rather than a margin, which would collapse on an empty div. -->
				<div class="h-8" aria-hidden="true" />
			{/if}

			<!-- The `!` is load-bearing: flowbite's Button carries `px-5`, and Tailwind
			     emits the shorthand `.p-2` BEFORE the directional `.px-5`, so the plain
			     form loses on source order however the class attribute is written —
			     leaving a button wider than the rail with its icon off the centre line.
			     `!p-2` keeps a real hit target while still fitting the strip. -->
			<div class="flex flex-wrap justify-center items-center gap-2" class:flex-col={$isSidebarRail}>
				<ThemeSwitcherButton
					class={$isSidebarRail ? "!p-2 w-full bg-transparent border-none" : "!p-2"}
					iconClass="text-zinc-700 dark:text-white"
				/>
			</div>
		</SidebarWrapper>
	</Sidebar>

	<div class="text-gray-600 dark:text-gray-500 text-sm text-center mt-8">
		<p class:hidden={$isSidebarRail}>
			<a
				class="font-bold hover:underline text-gray-700 dark:text-gray-400 hover:text-black dark:hover:text-white"
				href="/"
			>{site.shortname}</a>
			is an
			<a
				class="font-bold hover:underline text-gray-700 dark:text-gray-400 hover:text-black dark:hover:text-white"
				href="https://abstract3d.com"
				target="_blank">Abstract</a>
			product.<br />All rights reserved.
		</p>

		<div>
			<a href="https://abstract3d.com" target="_blank">
				<img
					class="{$isSidebarRail ? 'max-w-[24px]' : 'max-w-[48px]'} dark:invert opacity-70 dark:opacity-30 inline mt-4 hover:opacity-100 hover:dark:opacity-100 rounded-sm"
					src="/Abstract-LogoIcon-Black-Transparent.png"
					alt="Abstract"
				/>
			</a>
		</div>

		<div class="mt-4 flex justify-center gap-2" class:hidden={$isSidebarRail}>
			<a href="https://x.com/abstract3d" title="Follow Abstract on Twitter" target="_blank">
				<svg
					class="w-4 h-4 inline text-gray-500 dark:text-gray-600 hover:text-black dark:hover:text-white"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 20 20"
				>
					<path
						fill="currentColor"
						d="M12.186 8.672 18.743.947h-2.927l-5.005 5.9-4.44-5.9H0l7.434 9.876-6.986 8.23h2.927l5.434-6.4 4.82 6.4H20L12.186 8.672Zm-2.267 2.671L8.544 9.515 3.2 2.42h2.2l4.312 5.719 1.375 1.828 5.731 7.613h-2.2l-4.699-6.237Z"
					/>
				</svg>
			</a>

			<a href="https://discord.gg/ra5cCvf52s" title="Join Abstract on Discord" target="_blank">
				<svg
					class="w-5 h-5 inline text-gray-500 dark:text-gray-600 hover:text-black dark:hover:text-white"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
				>
					<path
						fill="currentColor"
						d="M18.942 5.556a16.3 16.3 0 0 0-4.126-1.3 12.04 12.04 0 0 0-.529 1.1 15.175 15.175 0 0 0-4.573 0 11.586 11.586 0 0 0-.535-1.1 16.274 16.274 0 0 0-4.129 1.3 17.392 17.392 0 0 0-2.868 11.662 15.785 15.785 0 0 0 4.963 2.521c.41-.564.773-1.16 1.084-1.785a10.638 10.638 0 0 1-1.706-.83c.143-.106.283-.217.418-.331a11.664 11.664 0 0 0 10.118 0c.137.114.277.225.418.331-.544.328-1.116.606-1.71.832a12.58 12.58 0 0 0 1.084 1.785 16.46 16.46 0 0 0 5.064-2.595 17.286 17.286 0 0 0-2.973-11.59ZM8.678 14.813a1.94 1.94 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.918 1.918 0 0 1 1.8 2.047 1.929 1.929 0 0 1-1.8 2.045Zm6.644 0a1.94 1.94 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.919 1.919 0 0 1 1.8 2.047 1.93 1.93 0 0 1-1.8 2.045Z"
					/>
				</svg>
			</a>
		</div>
	</div>
</Drawer>

<div class="flex px-4 w-full h-full min-w-0">
	<main class="flex-1 min-w-0 mx-auto mt-10 mb-40 w-full {$isSidebarRail ? 'sm:ml-[85px] xl:ml-[85px]' : 'sm:ml-64 xl:ml-72'} xl:mr-8 dark:text-white">
		<slot>
			<!-- The router mounts whether or not anyone is signed in (Design §14). It used to wait
			     for `isAuthorizationPending` to clear *and* for an access role, which meant every
			     public route was dead: `initialize()` redirected first, and even without the
			     redirect the access-role gate is unsatisfiable with no token. Route guarding is
			     the `publicRoutes` allowlist's job now — one list, rather than a condition here
			     plus a check in every page. -->
			{#if !$isAuthorizationPending && $isConfigurationLoaded && (!$isAuthorized || AbstractID.isUserApplicationAccessRoleAvailable)}
				<Router {routes} restoreScrollState={true} on:conditionsFailed={() => push('/')} />
			{:else if $isAuthorized && !AbstractID.isUserApplicationAccessRoleAvailable}
				<Alert>
					<Heading tag="h1">
						<i class="fa-solid fa-lock mr-2"></i>Access Denied
					</Heading>
					<P>
						You don't have permission to access {site.name}. Please contact an administrator to request access.
					</P>
				</Alert>
			{:else if ($configurationError ?? '').length > 0}
				<Alert>
					<Heading tag="h1">
						<i class="fa-solid fa-bug mr-2"></i>No Backend Response
					</Heading>
					<P>
						{site.name} is currently unavailable. Please try again later.
					</P>
				</Alert>
			{:else}
				<Spinner />
			{/if}
			<ToastContainer />
		</slot>
	</main>
</div>
